import { FC, useMemo, useState } from "react";
import ColorPickerWithAlpha from "src/components/ui-components/ColorPickerWithAlpha";
import { AppThemeFontProperty, FontSizeMaster, MasterModel } from "../Types";
import {
  Autocomplete,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import { useAppThemeContext } from "src/contexts/AppThemeContext";
import { ArrowDropDown, ArrowDropUp } from "@material-ui/icons";
import { useConfig } from "src/hooks/useConfig";

type FontPopoverProps = {
  property: AppThemeFontProperty;
};

const useStyles = makeStyles(() => ({
  inputRoot: {
    '& input': {
      paddingRight: '40px',
    },
  },
  adornmentBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginRight: '-8px',
  },
  arrowBtn: {
    padding: 0,
    height: '16px',
    width: '24px',
    minWidth: 0,
  },
}));

const FontPopover: FC<FontPopoverProps> = ({ property }) => {
  const { masterOptions, updateFontData } = useAppThemeContext();
  const classes = useStyles();
  const { isAdminUser } = useConfig();

  const fontWeights = useMemo(() => masterOptions.fontWeights.map(w => w.Value), [masterOptions.fontWeights]);
  const fontSizes = useMemo(() => masterOptions.fontSizes.map(s => s.FontSize), [masterOptions.fontSizes]);

  const maxFontSize = Math.max(
  ...masterOptions.fontSizes.map(option => Number(option.FontSize))
  );

  const maxFontWeight = Math.max(
    ...masterOptions.fontWeights
      .map(option => parseInt(option.Value.replace(/[^\d]/g, ""), 10))
      .filter(weight => !isNaN(weight))
  );

  const [state, setState] = useState({
    family: property.family,
    size: property.size,
    colour: property.colour,
    weight: property.weight,
    alpha: property.alpha ?? 1,
  });

  const currentWeightIndex = fontWeights.indexOf(state.weight);
  const currentSizeIndex = fontSizes.indexOf(state.size?.toString());

  const updateStateAndBackend = (partialUpdate: Partial<typeof state>) => {
    setState(prev => {
      const updated = { ...prev, ...partialUpdate };
      updateFontData(
        property.themeFontPropertyKey,
        updated.family,
        Number(updated.size),
        updated.colour,
        updated.weight,
        Number(Number(updated.alpha).toFixed(2))
      );
      return updated;
    });
  };

  const handleSizeUp = () => {
    if (currentSizeIndex < fontSizes.length - 1) {
      updateStateAndBackend({ size: Number(fontSizes[currentSizeIndex + 1]) });
    }
  };

  const handleSizeDown = () => {
    if (currentSizeIndex > 0) {
      updateStateAndBackend({ size: Number(fontSizes[currentSizeIndex - 1]) });
    }
  };

  const handleWeightUp = () => {
    if (currentWeightIndex < fontWeights.length - 1) {
      updateStateAndBackend({ weight: fontWeights[currentWeightIndex + 1] });
    }
  };

  const handleWeightDown = () => {
    if (currentWeightIndex > 0) {
      updateStateAndBackend({ weight: fontWeights[currentWeightIndex - 1] });
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography sx={{ marginBottom: '2%' }}>{property.label}</Typography>
        </Grid>

        <Grid item xs={12}>
          <ColorPickerWithAlpha
            propKey={property.themeFontPropertyKey}
            color={state.colour}
            alpha={state.alpha}
            handleChange={(key, value) => {
              if (typeof value === 'object') {
                updateStateAndBackend({
                  colour: (value as any).color,
                  alpha: Number(Number((value as any).alpha).toFixed(2)),
                });
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            options={masterOptions.fontFamilies}
            renderInput={(params) => <TextField {...params} label="Font Family" variant="outlined" />}
            style={{ marginBottom: '16px' }}
            onChange={(event, newValue: MasterModel) => updateStateAndBackend({ family: newValue.Value })}
            value={masterOptions.fontFamilies?.find((option) => option.Value === state.family) || null}
            getOptionLabel={(option) => option.Label}
            getOptionSelected={(option, value) => option.Value === state.family}
            disableClearable
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            freeSolo={isAdminUser}
            popupIcon={null}
            disableClearable
            options={masterOptions.fontWeights}
            getOptionLabel={(option: MasterModel) => typeof option === 'string' ? option : option?.Label ?? ''}
            value={
              masterOptions.fontWeights?.find((option) => option.Value === state.weight) ||
              state.weight || ''
            }
            onChange={(_, newValue: MasterModel | string) => {
              const newWeight = typeof newValue === 'string' ? newValue : newValue?.Value || '';
              // Add validation here too for consistency
              const numWeight = Number(newWeight);
              if (!isNaN(numWeight) && numWeight <= maxFontWeight) {
                updateStateAndBackend({ weight: newWeight });
              } else if (typeof newValue !== 'string') {
                // If it's from dropdown selection, allow it regardless
                updateStateAndBackend({ weight: newWeight });
              }
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (isAdminUser && reason === 'input') {
                const numValue = Number(newInputValue);
                // Also check for minimum value and handle empty string
                if (newInputValue === '' || (!isNaN(numValue) && numValue >= 1 && numValue <= maxFontWeight)) {
                  updateStateAndBackend({ weight: newInputValue });
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Font Weight"
                variant="outlined"
                size="small"
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  min: 1,
                  max: maxFontWeight,
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab', 'Backspace', 'Delete'];
                  
                    if (navigationKeys.includes(e.key)) {
                      if (params.inputProps?.onKeyDown) {
                        params.inputProps.onKeyDown(e);
                      }
                      return;
                    }

                    if (isAdminUser && e.currentTarget.value) {
                      const currentValue = Number(e.currentTarget.value);
                      const newChar = e.key;

                      if (!isNaN(Number(newChar)) && !isNaN(currentValue)) {
                        const potentialValue = Number(e.currentTarget.value + newChar);
                        if (potentialValue > maxFontWeight) {
                          e.preventDefault();
                        }
                      }
                    }
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  classes: { root: classes.inputRoot },
                  endAdornment: (
                    <InputAdornment position="end" className={classes.adornmentBox}>
                      <IconButton className={classes.arrowBtn} onClick={handleWeightUp} size="small">
                        <ArrowDropUp fontSize="small" />
                      </IconButton>
                      <IconButton className={classes.arrowBtn} onClick={handleWeightDown} size="small">
                        <ArrowDropDown fontSize="small" />
                      </IconButton>
                      {params.InputProps.endAdornment}
                    </InputAdornment>
                  )
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            freeSolo={isAdminUser}
            popupIcon={null}
            disableClearable
            options={masterOptions.fontSizes}
            getOptionLabel={(option) => typeof option === 'string' ? option : option?.FontSize ?? ''}
            value={
              masterOptions.fontSizes?.find((x) => x.FontSize === state.size?.toString()) ||
              state.size?.toString() ||
              ''
            }
            onChange={(_, newValue: FontSizeMaster | string | null) => {
              if (typeof newValue === 'string') {
                updateStateAndBackend({ size: Number(newValue) });
              } else if (newValue) {
                updateStateAndBackend({ size: Number(newValue.FontSize) });
              }
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (isAdminUser && reason === 'input') {
                const numValue = Number(newInputValue);
                if (!isNaN(numValue) && numValue <= maxFontSize) {
                  updateStateAndBackend({ size: numValue });
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Font Size"
                variant="outlined"
                size="small"
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  min: 1,
                  max: maxFontSize,
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    const navigationKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab', 'Backspace', 'Delete'];

                    if (navigationKeys.includes(e.key)) {
                      if (params.inputProps?.onKeyDown) {
                        params.inputProps.onKeyDown(e);
                      }
                      return;
                    }
                  
                    if (isAdminUser && e.currentTarget.value) {
                      const currentValue = Number(e.currentTarget.value);
                      const newChar = e.key;

                      // Only check for numeric keys
                      if (!isNaN(Number(newChar)) && !isNaN(currentValue)) {
                        const potentialValue = Number(e.currentTarget.value + newChar);
                        if (potentialValue > maxFontSize) {
                          e.preventDefault();
                        }
                      }
                    }
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  classes: { root: classes.inputRoot },
                  endAdornment: (
                    <InputAdornment position="end" className={classes.adornmentBox}>
                      <IconButton className={classes.arrowBtn} onClick={handleSizeUp} size="small">
                        <ArrowDropUp fontSize="small" />
                      </IconButton>
                      <IconButton className={classes.arrowBtn} onClick={handleSizeDown} size="small">
                        <ArrowDropDown fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default FontPopover;
